import React from 'react'

import {
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom'

import {
  CustomRouter,
  URLBuilder,
  UserTrackerProvider,
} from '@termly-web/common'

import Paths from 'constants/paths'

import lazyLoad from 'utils/lazyLoad'
import withSuspense from 'utils/withSuspense'

// This cannot be lazy-loaded because iframe-resizer (used in policy-viewer)
// cannot deal with it.
import PolicyNotFound from 'pages/PageNotFoundPage/PolicyNotFound'

import BannerSettingsRedirect from './BannerSettingsRedirect'
import history from './history'
import LoginRedirect from './LoginRedirect'
import TrackedPage from './TrackedPage'

const CheckoutPage = lazyLoad(() => import('pages/CheckoutPage'))
const LoginFailedPage = lazyLoad(() => import('pages/LoginFailedPage'))
const NonWebsitePages = lazyLoad(() => import('components/NonWebsitePages'))
const PageNotFoundPage = lazyLoad(() => import('pages/PageNotFoundPage'))
const SessionExpiredPage = lazyLoad(() => import('pages/SessionExpiredPage'))
const WebsitePages = lazyLoad(() => import('components/WebsitePages'))

// /user pages
const CreateAccountPage = lazyLoad(() => import('pages/CreateAccountPage'))
const ForgotPasswordPage = lazyLoad(() => import('pages/ForgotPasswordPage'))
const LogOutPage = lazyLoad(() => import('pages/LogOutPage'))
const MigrateAccountPage = lazyLoad(() => import('pages/MigrateAccountPage'))
const ResetPasswordPage = lazyLoad(() => import('pages/ResetPasswordPage'))
const SignInPage = lazyLoad(() => import('pages/SignInPage'))
const SignUpPage = lazyLoad(() => import('pages/SignUpPage'))

// Non-website pages
const AccountPage = lazyLoad(() => import('pages/AccountPage'))
const CMPOnboardingPage = lazyLoad(() => import('pages/CMPOnboardingPage'))
const WebsiteLimitPage = lazyLoad(() => import('pages/WebsiteLimitPage'))
const BillingHistoryPage = lazyLoad(() => import('pages/BillingHistoryPage'))
const BillingInfoPage = lazyLoad(() => import('pages/BillingInfoPage'))
const CheckoutPreviewPage = lazyLoad(() => import('pages/CheckoutPreviewPage'))
const DashboardIndexPage = lazyLoad(() => import('pages/DashboardIndexPage'))
const DeleteAccountPage = lazyLoad(() => import('pages/DeleteAccountPage'))
const PlanInfoPage = lazyLoad(() => import('pages/PlanInfoPage'))
const ReceiptPage = lazyLoad(() => import('pages/ReceiptPage'))
const SiteAuditPage = lazyLoad(() => import('pages/SiteAuditPage'))
const SubscriptionManagerPage = lazyLoad(() => import('pages/SubscriptionManagerPage'))
const SupportPage = lazyLoad(() => import('pages/SupportPage'))
const ThreeDSAuthCompletePage = lazyLoad(() => import('pages/ThreeDSAuthCompletePage'))
const ThreeDSAuthFailedPage = lazyLoad(() => import('pages/ThreeDSAuthFailedPage'))
const UserManagementPage = lazyLoad(() => import('pages/UserManagementPage'))
const WebsiteManagementPage = lazyLoad(() => import('pages/WebsiteManagementPage'))

// Website pages
const APIKeyPage = lazyLoad(() => import('pages/APIKeyPage'))
const AddCookiePage = lazyLoad(() => import('pages/AddCookiePage'))
const AddNewWebsitePage = lazyLoad(() => import('pages/AddNewWebsitePage'))
const BannerSettingsPage = lazyLoad(() => import('pages/BannerSettingsPage'))
const BusinessInfoPage = lazyLoad(() => import('pages/BusinessInfoPage'))
const CCPASettingsPage = lazyLoad(() => import('pages/CCPASettingsPage'))
const ConsentLogsPage = lazyLoad(() => import('pages/ConsentLogsPage'))
const DomainRulesPage = lazyLoad(() => import('pages/DomainRulesPage'))
const DSARFormPage = lazyLoad(() => import('pages/DSARFormPage'))
const DudaIntegrationPage = lazyLoad(() => import('pages/DudaIntegrationPage'))
const EditCookiePage = lazyLoad(() => import('pages/EditCookiePage'))
const EditWebsiteInfoPage = lazyLoad(() => import('pages/EditWebsiteInfoPage'))
const GCMValidatorPage = lazyLoad(() => import('pages/GCMValidatorPage'))
const PolicyPage = lazyLoad(() => import('pages/PolicyPage'))
const ScanReportPage = lazyLoad(() => import('pages/ScanReportPage'))
const ScanSettingsPage = lazyLoad(() => import('pages/ScanSettingsPage'))
const VendorsPage = lazyLoad(() => import('pages/VendorsPage'))
const WebsiteDashboardPage = lazyLoad(() => import('pages/WebsiteDashboardPage'))

const PagesNotRequiringUserRecord = lazyLoad(() => import('./PagesNotRequiringUserRecord'))
const PagesRequiringUserRecord = lazyLoad(() => import('./PagesRequiringUserRecord'))


export default function Router() {
  return (
    <CustomRouter
      history={ history }
    >
      <Routes>
        <Route
          element={ <Navigate to={ Paths.DASHBOARD } /> }
          path="/"
        />

        <Route
          element={ <Navigate to={ Paths.RECEIPT() } /> }
          path={ Paths.LEGACY_RECEIPT() }
        />

        <Route
          element={ <Navigate to={ Paths.DASHBOARD } /> }
          path="/dashboard/website"
        />

        <Route
          index
          element={ <LoginRedirect /> }
        />

        <Route
          element={ withSuspense(DudaIntegrationPage) }
          path={ URLBuilder.dudaIntegrationURL() }
        />

        <Route
          element={ withSuspense(ThreeDSAuthCompletePage) }
          path={ URLBuilder.threeDSAuthCompleteURL() }
        />

        <Route
          element={ <UserTrackerProvider><Outlet /></UserTrackerProvider> }
        >
          <Route
            element={ withSuspense(LogOutPage) }
            path={ URLBuilder.logoutURL() }
          />

          <Route
            element={ withSuspense(LoginFailedPage) }
            path={ Paths.LOGIN_FAILED }
          />

          <Route
            element={ withSuspense(SessionExpiredPage) }
            path={ Paths.SESSION_EXPIRED }
          />

          <Route element={ withSuspense(PagesNotRequiringUserRecord) }>
            <Route
              element={ withSuspense(SignInPage) }
              path={ Paths.LOGIN }
            />

            <Route
              element={ withSuspense(SignUpPage) }
              path={ URLBuilder.signUpURL() }
            />

            <Route
              element={ withSuspense(ForgotPasswordPage) }
              path={ Paths.FORGOT_PWD }
            />

            <Route
              element={ withSuspense(ResetPasswordPage) }
              path={ URLBuilder.resetPasswordURL() }
            />

            <Route
              element={ withSuspense(CreateAccountPage) }
              path={ Paths.CREATE_ACCOUNT }
            />

            <Route
              element={ withSuspense(MigrateAccountPage) }
              path={ Paths.MIGRATE_ACCOUNT }
            />
          </Route>

          <Route element={ withSuspense(PagesRequiringUserRecord) }>
            <Route
              element={ withSuspense(DashboardIndexPage) }
              path={ Paths.DASHBOARD }
            />

            <Route
              element={
                <TrackedPage
                  component={ CheckoutPage }
                  trackingName="Checkout"
                />
              }
              path={ Paths.CHECKOUT() }
            />

            <Route
              element={
                <TrackedPage
                  component={ SiteAuditPage }
                  trackingName="Site Audit"
                />
              }
              path={ Paths.AUDIT }
            />

            <Route
              element={ withSuspense(WebsitePages) }
              path={ URLBuilder.websiteDashboardURL() }
            >
              <Route
                index
                element={
                  <TrackedPage
                    component={ WebsiteDashboardPage }
                    trackingName="Website Dashboard"
                  />
                }
              />
              <Route
                element={
                  <TrackedPage
                    component={ DomainRulesPage }
                    trackingName="Domain Rules"
                  />
                }
                path={ `${ URLBuilder.domainRulesURL() }/*` }
              />
              <Route
                element={
                  <TrackedPage
                    component={ VendorsPage }
                    trackingName="Vendors"
                  />
                }
                path={ URLBuilder.websiteVendorsPageURL() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ CMPOnboardingPage }
                    trackingName="CMP Onboarding"
                  />
                }
                path={ URLBuilder.websiteCMPOnboardingURL() }
              />
              <Route
                element={ withSuspense(GCMValidatorPage) }
                path={ URLBuilder.websiteGCMValidatorURL() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ AddCookiePage }
                    trackingName="Add Cookie"
                  />
                }
                path={ Paths.REPORT_ADD_COOKIE() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ EditCookiePage }
                    trackingName="Edit Cookie"
                  />
                }
                path={ Paths.REPORT_EDIT_COOKIE() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ ScanReportPage }
                    trackingName="Cookie Report"
                  />
                }
                path={ Paths.COOKIE_REPORT() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ ScanSettingsPage }
                    trackingName="Scan Settings"
                  />
                }
                path={ Paths.SCAN_SETTINGS() }
              />
              <Route
                element={ withSuspense(EditWebsiteInfoPage) }
                path={ Paths.WEBSITE_INFO() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ BusinessInfoPage }
                    trackingName="Business Info"
                  />
                }
                path={ Paths.BUSINESS_INFO() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ ConsentLogsPage }
                    trackingName="Consent Logs"
                  />
                }
                path={ Paths.CONSENT_LOGS() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ APIKeyPage }
                    trackingName="API Key"
                  />
                }
                path={ Paths.API_KEY() }
              />

              { /* BEGIN DEPRECATED URLS */ }
              <Route
                element={ <BannerSettingsRedirect /> }
                path={ Paths.EMBED_CONSENT_BANNER() }
              />
              <Route
                element={ <BannerSettingsRedirect /> }
                path={ Paths.EMBED_PREFERENCE_CENTER_BUTTON() }
              />
              { /* END DEPRECATED URLS */ }

              <Route
                element={
                  <TrackedPage
                    component={ CCPASettingsPage }
                    trackingName="CCPA Settings"
                  />
                }
                path={ Paths.CCPA_SETTINGS() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ DSARFormPage }
                    trackingName="DSAR Form"
                  />
                }
                path={ Paths.DSAR_FORM() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ BannerSettingsPage }
                    trackingName="Banner Settings"
                  />
                }
                path={ Paths.BANNER_SETTINGS() }
              />
              <Route
                element={ withSuspense(PolicyPage) }
                path={ URLBuilder.policyDetailsURL() }
              />
            </Route>

            <Route
              element={ withSuspense(NonWebsitePages) }
              path={ Paths.DASHBOARD }
            >
              <Route
                element={
                  <TrackedPage
                    component={ CMPOnboardingPage }
                    trackingName="CMP Onboarding"
                  />
                }
                path={ Paths.CMP_ONBOARDING }
              />
              <Route
                element={ withSuspense(AddNewWebsitePage) }
                path={ Paths.NEW_WEBSITE }
              />
              <Route
                element={
                  <TrackedPage
                    component={ WebsiteLimitPage }
                    trackingName="Website Limit"
                  />
                }
                path={ Paths.WEBSITE_LIMIT }
              />
              <Route
                element={
                  <TrackedPage
                    component={ DeleteAccountPage }
                    trackingName="Delete Account"
                  />
                }
                path={ Paths.DELETE_ACCOUNT }
              />
              <Route
                element={
                  <TrackedPage
                    component={ UserManagementPage }
                    trackingName="User Management"
                  />
                }
                path={ Paths.USER_MANAGEMENT }
              />
              <Route
                element={
                  <TrackedPage
                    component={ WebsiteManagementPage }
                    trackingName="Website Management"
                  />
                }
                path={ URLBuilder.websitesURL() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ AccountPage }
                    trackingName="Account Settings"
                  />
                }
                path={ Paths.ACCOUNT }
              />

              <Route
                element={
                  <TrackedPage
                    component={ BillingHistoryPage }
                    trackingName="Billing History"
                  />
                }
                path={ Paths.BILLING_HISTORY }
              />
              <Route
                element={
                  <TrackedPage
                    component={ BillingInfoPage }
                    trackingName="Billing Info"
                  />
                }
                path={ Paths.BILLING }
              />
              <Route
                element={
                  <TrackedPage
                    component={ CheckoutPreviewPage }
                    trackingName="Checkout Preview"
                  />
                }
                path={ Paths.CHECKOUT_PREVIEW }
              />
              <Route
                element={
                  <TrackedPage
                    component={ SupportPage }
                    trackingName="Support"
                  />
                }
                path={ URLBuilder.supportURL() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ ReceiptPage }
                    trackingName="Receipt"
                  />
                }
                path={ Paths.RECEIPT() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ PlanInfoPage }
                    trackingName="Plan Info"
                  />
                }
                path={ Paths.PLAN_INFO }
              />
              <Route
                element={ withSuspense(ThreeDSAuthFailedPage) }
                path={ URLBuilder.threeDSAuthFailedURL() }
              />
              <Route
                element={
                  <TrackedPage
                    component={ SubscriptionManagerPage }
                    trackingName="Subscription Manager"
                  />
                }
                path={ URLBuilder.subscriptionManagerURL() }
              />
            </Route>

            <Route
              element={ withSuspense(PageNotFoundPage) }
              path="*"
            />
          </Route>
        </Route>

        <Route
          exact
          element={ null }
          path="/ping"
        />

        <Route
          exact
          element={ <PolicyNotFound /> }
          path={ Paths.POLICY_NOT_FOUND }
        />

        <Route
          element={ withSuspense(PageNotFoundPage) }
          path="*"
        />
      </Routes>
    </CustomRouter>
  )
}
